import { Suspense, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { TabBar } from "@components/ui";
import { OfflineBanner } from "@components/OfflineBanner";
import { InstallPrompt } from "@components/InstallPrompt";
import { CallOverlay } from "@components/CallOverlay";
import { MobileTabBar } from "@components/MobileTabBar";
import { DialerSheet } from "@components/DialerSheet";
import { RouteFallback } from "@components/RouteFallback";
import { VoiceProvider, useVoice } from "@lib/voiceContext";
import { DialerContext } from "@lib/dialerContext";
import {
  PhoneIcon, MessageIcon, VideoIcon, ContactsIcon, SettingsIcon, NumberIcon, ShieldIcon,
  InstallIcon, LogoutIcon, ChartIcon, EllipsisIcon, HomeIcon,
} from "@icons";
import { useAuth } from "@hooks/useAuth";
import { useOnlineStatus } from "@hooks/useOnlineStatus";
import { usePwaInstall } from "@hooks/usePwaInstall";
import { useServiceWorkerMessages } from "@hooks/useServiceWorkerMessages";
import { useApnsRegistration } from "@hooks/useApnsRegistration";
import { useVoipRegistration } from "@hooks/useVoipRegistration";
import { useRealtime } from "@hooks/useRealtime";
import styles from "./DashboardPage.module.css";

const NAV_ITEMS = [
  { key: "home", label: "Home", icon: <HomeIcon /> },
  { key: "calls", label: "Calls", icon: <PhoneIcon /> },
  { key: "messages", label: "Messages", icon: <MessageIcon /> },
  { key: "video", label: "Video", icon: <VideoIcon /> },
  { key: "contacts", label: "Contacts", icon: <ContactsIcon /> },
  { key: "numbers", label: "Numbers", icon: <NumberIcon /> },
  { key: "insights", label: "Insights", icon: <ChartIcon /> },
  { key: "settings", label: "Settings", icon: <SettingsIcon /> },
];

function pathToTab(path: string): string {
  const seg = path.split("/")[2] ?? "home";
  return NAV_ITEMS.some((n) => n.key === seg) || seg === "admin" || seg === "more" ? seg : "home";
}

// Mobile floating bar (Aria iOS v2): Home + Calls, center call FAB, Messages
// + More. Everything else (Contacts, Video, Numbers, Insights, Settings,
// Admin, sign-out) lives on the More page.
const MOBILE_PRIMARY = ["home", "calls", "messages"] as const;

export function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { online } = useOnlineStatus();
  const { canInstall, promptInstall } = usePwaInstall();

  const activeTab = pathToTab(location.pathname);
  // Native iOS: register the APNs device token once signed in (web no-op).
  useApnsRegistration(Boolean(user));
  // Native iOS: bind a Twilio token to the PushKit token so incoming calls
  // ring via CallKit even with the app closed (web no-op).
  useVoipRegistration(Boolean(user));
  // Live updates over WebSocket (falls back to polling when disconnected).
  useRealtime(Boolean(user));
  const isAdmin = user?.role === "SUPERADMIN";
  const items = isAdmin
    ? [...NAV_ITEMS, { key: "admin", label: "Admin", icon: <ShieldIcon /> }]
    : NAV_ITEMS;

  const go = (k: string) => navigate(`/dashboard/${k === "home" ? "" : k}`);

  const mobileItems = useMemo(
    () => [
      ...items.filter((i) => (MOBILE_PRIMARY as readonly string[]).includes(i.key)),
      { key: "more", label: "More", icon: <EllipsisIcon /> },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin]
  );
  // Anything not in the primary tabs lights up the More tab, matching iOS.
  const mobileActive = (MOBILE_PRIMARY as readonly string[]).includes(activeTab) ? activeTab : "more";

  const [dialerOpen, setDialerOpen] = useState(false);
  const dialerCtx = useMemo(() => ({ openDialer: () => setDialerOpen(true) }), []);

  const label = user?.displayName ?? user?.email ?? "—";
  const role = (user?.role ?? "").toLowerCase();
  const initials = (user?.displayName ?? user?.email ?? "?")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("") || "?";

  return (
    <VoiceProvider>
      <DialerContext.Provider value={dialerCtx}>
      <div className={styles.page}>
        <div className={styles.overlays}>
          {!online && <OfflineBanner />}
          <InstallPrompt />
        </div>
        <div className={styles.window}>
          <aside className={styles.sidebar}>
            <div className={styles.brand}>
              <img src="/icons/icon.svg" alt="" className={styles.brandLogo} width={29} height={29} />
              <span className={styles.brandName}>Aria</span>
            </div>
            <TabBar variant="sidebar" items={items} active={activeTab} onChange={go} />
            <div className={styles.user}>
              <div className={styles.userAvatar} aria-hidden>{initials}</div>
              <div className={styles.userMeta}>
                <div className={styles.userName}>{label}</div>
                <div className={styles.userRole}>
                  <span className={styles.statusDot} aria-hidden />
                  {role ? `${role.charAt(0).toUpperCase()}${role.slice(1)} · online` : "online"}
                </div>
              </div>
              {canInstall ? (
                <button className={styles.chipBtn} onClick={() => void promptInstall()} aria-label="Install app" title="Install app">
                  <InstallIcon width={17} height={17} />
                </button>
              ) : (
                <button className={styles.chipBtn} onClick={logout} aria-label="Sign out" title="Sign out">
                  <LogoutIcon width={17} height={17} />
                </button>
              )}
            </div>
          </aside>

          <main className={styles.main}>
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </main>

          <MobileTabBar
            items={mobileItems}
            active={mobileActive}
            onChange={go}
            onCall={() => setDialerOpen(true)}
          />
        </div>

        <DialerSheet open={dialerOpen} onClose={() => setDialerOpen(false)} />
        <CallOverlayHost />
        <ServiceWorkerBridge />
      </div>
      </DialerContext.Provider>
    </VoiceProvider>
  );
}

// useVoice() must run *inside* VoiceProvider, so the CallOverlay lives here.
function CallOverlayHost() {
  const voice = useVoice();
  return <CallOverlay voice={voice} />;
}

// Bridges service-worker messages (notification actions, call answer/decline)
// into voice actions + navigation. Lives inside VoiceProvider for useVoice().
function ServiceWorkerBridge() {
  useServiceWorkerMessages();
  return null;
}
